import { NEW_APP_URL } from "@/config/retired";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Scale, Mail, Lock, User, ArrowLeft, Briefcase, Building, MapPin, Phone, AlertTriangle } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Helmet } from "react-helmet-async";
import { useRateLimit } from "@/hooks/useRateLimit";
import { Alert, AlertDescription } from "@/components/ui/alert";

const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [cargo, setCargo] = useState("");
  const [organizacion, setOrganizacion] = useState("");
  const [provincia, setProvincia] = useState("");
  const [celular, setCelular] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [searchParams] = useSearchParams();
  const { signIn, user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { isBlocked, message: rateLimitMessage, checkRateLimit, resetState: resetRateLimit } = useRateLimit();

  const isResetMode = searchParams.get('reset') === 'true';

  useEffect(() => {
    // Don't redirect if user is in password reset mode
    if (user && !isResetMode) {
      const returnTo = searchParams.get('returnTo');
      if (returnTo) {
        navigate(decodeURIComponent(returnTo));
      } else {
        navigate("/");
      }
    }
  }, [user, navigate, isResetMode, searchParams]);

  useEffect(() => {
    // Handle password reset flow
    if (isResetMode) {
      setIsResettingPassword(true);
    }
  }, [isResetMode]);

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check rate limit before proceeding
    const actionType = isLogin ? "login" : "signup";
    const allowed = await checkRateLimit(email.toLowerCase(), actionType);
    
    if (!allowed) {
      toast({
        title: "Demasiados intentos",
        description: rateLimitMessage || "Por favor espera antes de intentar de nuevo.",
        variant: "destructive",
      });
      return;
    }
    
    setLoading(true);

    try {
      if (isLogin) {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        
        if (error) {
          // Check if error is due to unconfirmed email
          if (error.message.includes('Email not confirmed')) {
            toast({
              title: "Email no confirmado",
              description: "Por favor revisa tu correo y confirma tu cuenta antes de iniciar sesión. Revisa también la carpeta de spam.",
              variant: "destructive",
              duration: 10000,
            });
          } else {
            throw error;
          }
          setLoading(false);
          return;
        }
        
        if (data.user) {
          toast({
            title: "¡Bienvenido de vuelta!",
            description: "Has iniciado sesión exitosamente.",
          });
          const returnTo = searchParams.get('returnTo');
          if (returnTo) {
            navigate(decodeURIComponent(returnTo));
          } else {
            navigate("/");
          }
        }
      } else {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/`,
            data: {
              name: name,
              cargo: cargo,
              organizacion: organizacion,
              provincia: provincia,
              celular: celular,
            }
          }
        });
        
        if (error) throw error;
        
        if (data.user) {
          // Check if email confirmation is required
          if (data.user.email_confirmed_at) {
            toast({
              title: "¡Cuenta creada!",
              description: "Tu cuenta está lista. Redirigiendo...",
            });
            // User was auto-confirmed (Google OAuth) - redirect
            setTimeout(() => navigate("/"), 1500);
          } else {
            toast({
              title: "¡Cuenta creada!",
              description: "Por favor revisa tu correo para verificar tu cuenta. Revisa también la carpeta de spam.",
              duration: 10000,
            });
            // Switch to login view and clear sensitive fields
            setIsLogin(true);
            setPassword("");
            setName("");
            setCargo("");
            setOrganizacion("");
            setProvincia("");
            setCelular("");
          }
        }
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Algo salió mal. Por favor intenta de nuevo.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast({
        title: "Error",
        description: "Las contraseñas no coinciden.",
        variant: "destructive",
      });
      return;
    }

    if (newPassword.length < 6) {
      toast({
        title: "Error",
        description: "La contraseña debe tener al menos 6 caracteres.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;

      toast({
        title: "¡Contraseña actualizada!",
        description: "Tu contraseña ha sido cambiada exitosamente.",
      });

      // Navigate to main page or return URL after successful reset
      const returnTo = searchParams.get('returnTo');
      if (returnTo) {
        navigate(decodeURIComponent(returnTo));
      } else {
        navigate("/");
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Error al cambiar la contraseña.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      toast({
        title: "Error",
        description: "Por favor ingresa tu correo electrónico.",
        variant: "destructive",
      });
      return;
    }

    // Check rate limit for password reset
    const allowed = await checkRateLimit(email.toLowerCase(), "password_reset");
    if (!allowed) {
      toast({
        title: "Demasiados intentos",
        description: rateLimitMessage || "Por favor espera antes de intentar de nuevo.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth?reset=true`,
      });

      if (error) throw error;

      toast({
        title: "Correo enviado",
        description: "Revisa tu correo para restablecer tu contraseña.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Error al enviar correo de restablecimiento.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    if (!email) {
      toast({
        title: "Error",
        description: "Por favor ingresa tu correo electrónico.",
        variant: "destructive",
      });
      return;
    }

    // Check rate limit for resend confirmation
    const allowed = await checkRateLimit(email.toLowerCase(), "resend_confirmation");
    if (!allowed) {
      toast({
        title: "Demasiados intentos",
        description: rateLimitMessage || "Por favor espera antes de intentar de nuevo.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
        }
      });

      if (error) throw error;

      toast({
        title: "Correo reenviado",
        description: "Hemos reenviado el correo de confirmación. Revisa tu bandeja de entrada y spam.",
        duration: 8000,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Error al reenviar correo de confirmación.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    try {
      await signIn();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Error al iniciar sesión con Google.",
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>DoctIA | Autenticación</title>
        <meta name="description" content="Inicia sesión o registrate en DoctIA" />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-secondary/20">
      {/* Header */}
      <header className="border-b bg-background/80 backdrop-blur-md">
        <div className="container max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
          <img src="/inteligencialegal-logo-square.png" alt="DoctIA Logo" className="h-8 w-8" />
          <span className="text-2xl font-bold text-foreground">DoctIA</span>
        </Link>
          <div className="flex items-center gap-4">
            <ThemeToggle />
            <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver al Inicio
            </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Auth Form */}
      <div className="container max-w-md mx-auto px-6 py-16">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-2 text-foreground">
            {isResettingPassword ? "Restablecer Contraseña" : isLogin ? "Bienvenido de Vuelta" : "Comenzar"}
          </h1>
          <p className="text-muted-foreground">
            {isResettingPassword 
              ? "Ingresa tu nueva contraseña para restablecer el acceso a tu cuenta"
              : isLogin 
                ? "Inicia sesión en tu cuenta para continuar tu investigación legal" 
                : "Crea tu cuenta para empezar a usar DoctIA"
            }
          </p>
        </div>

        <Card className="border border-border shadow-card bg-card">
          <CardHeader>
            <CardTitle className="text-2xl text-center">
              {isResettingPassword ? "Nueva Contraseña" : isLogin ? "Iniciar Sesión" : "Crear Cuenta"}
            </CardTitle>
            <CardDescription className="text-center">
              {isResettingPassword 
                ? "Ingresa una nueva contraseña para tu cuenta"
                : isLogin 
                  ? "Ingresa tus credenciales para acceder a tu cuenta" 
                  : "Completa tus datos para crear una nueva cuenta"
              }
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Rate Limit Warning */}
            {isBlocked && rateLimitMessage && (
              <Alert variant="destructive" className="border-destructive/50 bg-destructive/10">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{rateLimitMessage}</AlertDescription>
              </Alert>
            )}
            {isResettingPassword ? (
              /* Password Reset Form */
              <form onSubmit={handlePasswordReset} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="newPassword" className="flex items-center gap-2">
                    <Lock className="h-4 w-4" />
                    Nueva Contraseña
                  </Label>
                  <Input
                    id="newPassword"
                    type="password"
                    placeholder="Ingresa tu nueva contraseña"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    className="border-primary/30 focus:border-primary transition-colors"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="flex items-center gap-2">
                    <Lock className="h-4 w-4" />
                    Confirmar Contraseña
                  </Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Confirma tu nueva contraseña"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="border-primary/30 focus:border-primary transition-colors"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-primary text-primary-foreground hover:bg-primary-hover transition-colors"
                  size="lg"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Cambiando Contraseña...
                    </div>
                  ) : (
                    "Cambiar Contraseña"
                  )}
                </Button>
              </form>
            ) : (
              <>
                {/* Google OAuth */}
                <Button
                  onClick={handleGoogleAuth}
                  variant="outline"
                  className="w-full border-border hover:border-primary hover:bg-primary/5 transition-colors"
                  size="lg"
                >
                  <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="currentColor"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  Continuar con Google
                </Button>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <Separator className="w-full" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">O continúa con correo</span>
                  </div>
                </div>

                {/* Email/Password Form */}
                <form onSubmit={handleEmailAuth} className="space-y-4">
              {!isLogin && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="lg:col-span-2 space-y-2">
                    <Label htmlFor="name" className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Nombre Completo
                    </Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="Ingresa tu nombre completo"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required={!isLogin}
                      className="border-primary/30 focus:border-primary transition-colors"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="cargo" className="flex items-center gap-2">
                      <Briefcase className="h-4 w-4" />
                      Cargo
                    </Label>
                    <Input
                      id="cargo"
                      type="text"
                      placeholder="Tu cargo o posición"
                      value={cargo}
                      onChange={(e) => setCargo(e.target.value)}
                      required={!isLogin}
                      className="border-primary/30 focus:border-primary transition-colors"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="organizacion" className="flex items-center gap-2">
                      <Building className="h-4 w-4" />
                      Organización
                    </Label>
                    <Input
                      id="organizacion"
                      type="text"
                      placeholder="Estudio, Tribunal, etc."
                      value={organizacion}
                      onChange={(e) => setOrganizacion(e.target.value)}
                      required={!isLogin}
                      className="border-primary/30 focus:border-primary transition-colors"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="provincia" className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      Provincia
                    </Label>
                    <Input
                      id="provincia"
                      type="text"
                      placeholder="Tu provincia"
                      value={provincia}
                      onChange={(e) => setProvincia(e.target.value)}
                      required={!isLogin}
                      className="border-primary/30 focus:border-primary transition-colors"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="celular" className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      Celular
                    </Label>
                    <Input
                      id="celular"
                      type="tel"
                      placeholder="Tu número de celular"
                      value={celular}
                      onChange={(e) => setCelular(e.target.value)}
                      required={!isLogin}
                      className="border-primary/30 focus:border-primary transition-colors"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Correo Electrónico
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Ingresa tu correo electrónico"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="border-primary/30 focus:border-primary transition-colors"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="flex items-center gap-2">
                  <Lock className="h-4 w-4" />
                  Contraseña
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Ingresa tu contraseña"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="border-primary/30 focus:border-primary transition-colors"
                />
              </div>

                  <Button
                    type="submit"
                    className="w-full bg-primary text-primary-foreground hover:bg-primary-hover transition-colors"
                    size="lg"
                    disabled={loading}
                  >
                    {loading ? (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        {isLogin ? "Iniciando Sesión..." : "Creando Cuenta..."}
                      </div>
                    ) : (
                      isLogin ? "Iniciar Sesión" : "Crear Cuenta"
                    )}
                  </Button>

                  {isLogin && (
                    <div className="text-center space-y-2">
                      <Button
                        type="button"
                        variant="link"
                        className="text-sm text-primary hover:text-primary-glow"
                        onClick={handleForgotPassword}
                        disabled={loading}
                      >
                        ¿Olvidaste tu contraseña?
                      </Button>
                      <Button
                        type="button"
                        variant="link"
                        className="text-sm text-muted-foreground hover:text-primary block"
                        onClick={handleResendConfirmation}
                        disabled={loading}
                      >
                        ¿No recibiste el correo de confirmación?
                      </Button>
                    </div>
                  )}
                </form>
              </>
            )}
          </CardContent>

          <CardFooter className="flex flex-col space-y-4">
            {!isResettingPassword && (
              <>
                <div className="text-center text-sm text-muted-foreground">
                  ¿No tenés una cuenta?
                  <a href={`${NEW_APP_URL}/login`} className="ml-2 text-primary hover:text-primary-glow underline">
                    Creala en la nueva versión
                  </a>
                </div>

                {!isLogin && (
                  <p className="text-xs text-center text-muted-foreground">
                    Al crear una cuenta, aceptás nuestros{" "}
                    <Link to="/ayuda#terminos-condiciones" className="text-primary hover:underline">
                      Términos de Servicio
                    </Link>{" "}
                    y{" "}
                    <Link to="/ayuda#privacy" className="text-primary hover:underline">
                      Política de Privacidad
                    </Link>
                  </p>
                )}
              </>
            )}

            {isResettingPassword && (
              <div className="text-center">
                <Button
                  variant="link"
                  className="text-sm text-primary hover:text-primary-glow"
                  onClick={() => {
                    setIsResettingPassword(false);
                    navigate('/auth');
                  }}
                >
                  Volver al inicio de sesión
                </Button>
              </div>
            )}
          </CardFooter>
        </Card>
      </div>
    </div>
    </>
  );
};

export default Auth;